import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, User as UserIcon, Trash2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  canEditBranches: "Branş & komisyon düzenleme",
  canEditSettings: "Hesaplama ayarları",
  canDeleteRecords: "Kayıt silme (poliçe/müşteri/branş)",
  canBulkImport: "Toplu içe aktarma (Excel/PDF)",
  canRunBackup: "Yedekleme çalıştırma",
  canViewReports: "Raporlar (Yıllık Üretim)",
};

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    setBusyId(uid);
    try {
      const { data } = await axios.patch(`${API}/admin/users/${uid}/role`, { role });
      setUsers((x) => x.map((u) => (u.id === uid ? data : u)));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Rol değiştirilemedi");
    } finally {
      setBusyId(null);
    }
  };

  const togglePerm = async (u, key) => {
    if (u.role === "admin") return;
    const next = { ...u.permissions, [key]: !u.permissions[key] };
    setUsers((x) => x.map((y) => (y.id === u.id ? { ...y, permissions: next } : y)));
    try {
      const { data } = await axios.patch(`${API}/admin/users/${u.id}/permissions`, { permissions: next });
      setUsers((x) => x.map((y) => (y.id === u.id ? data : y)));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "İzin güncellenemedi");
      load();
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısı ve tüm verileri silinsin mi?`)) return;
    setBusyId(u.id);
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      setUsers((x) => x.filter((y) => y.id !== u.id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Kullanıcı silinemedi");
    } finally {
      setBusyId(null);
    }
  };

  const admins = users.filter((u) => u.role === "admin").length;
  const stdUsers = users.length - admins;

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <div className="admin-panel-head">
        <div>
          <div className="eyebrow"><Shield size={13} /> YÖNETİM</div>
          <h1>Kullanıcılar & İzinler</h1>
          <p>Standart kullanıcıların her sayfa ve butondaki yetkilerini yönetin.</p>
        </div>
        <div className="admin-panel-stats">
          <div><strong>{admins}</strong><span>Yönetici</span></div>
          <div><strong>{stdUsers}</strong><span>Standart kullanıcı</span></div>
          <button className="ghost-button" data-testid="admin-refresh" onClick={load}><RefreshCw size={14} /> Yenile</button>
        </div>
      </div>

      {error && <div className="auth-error" data-testid="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Yükleniyor…</div>
      ) : (
        <div className="admin-users">
          {users.map((u) => (
            <div className={`admin-user-card ${u.role === "admin" ? "admin" : ""}`} key={u.id} data-testid={`admin-user-${u.id}`}>
              <div className="admin-user-head">
                <div className="admin-user-info">
                  {u.role === "admin" ? <Shield size={18} /> : <UserIcon size={18} />}
                  <div>
                    <strong>{u.name || "(isimsiz)"}</strong>
                    <span>{u.email}</span>
                  </div>
                </div>
                <div className="admin-user-actions">
                  <select
                    className="role-select"
                    data-testid={`admin-role-${u.id}`}
                    value={u.role}
                    disabled={busyId === u.id}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                  >
                    <option value="user">Standart kullanıcı</option>
                    <option value="admin">Yönetici</option>
                  </select>
                  <button className="delete-customer" data-testid={`admin-delete-${u.id}`} disabled={busyId === u.id} onClick={() => deleteUser(u)}>
                    <Trash2 size={14} /> Sil
                  </button>
                </div>
              </div>

              <div className="admin-perms">
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <label key={key} className={`perm-toggle ${u.role === "admin" ? "locked" : ""}`}>
                    <input
                      type="checkbox"
                      data-testid={`perm-${key}-${u.id}`}
                      checked={!!u.permissions?.[key]}
                      disabled={u.role === "admin"}
                      onChange={() => togglePerm(u, key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {u.role === "admin" && <div className="admin-lock-hint">Yöneticiler tüm izinlere sahiptir. Kısıtlamak için önce rolü Standart kullanıcı yapın.</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
